"use client"

import Link from "next/link"
import dynamic from "next/dynamic"
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { useRouter } from "next/navigation"
import { toast } from "sonner"
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion"
import {
  ArrowRight,
  Award,
  BarChart3,
  Brain,
  Briefcase,
  Building,
  CheckCircle2,
  Clock,
  Facebook,
  FileText,
  GraduationCap,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  ShieldCheck,
  Target,
  TrendingUp,
  Twitter,
  Users,
} from "lucide-react"
import { useAuth } from "@/hooks/use-auth"

// WebGL scenes load after first paint so they never block the page.
const HeroScene = dynamic(() => import("./scenes").then((mod) => mod.HeroScene), { ssr: false })
const FunnelScene = dynamic(() => import("./scenes").then((mod) => mod.FunnelScene), { ssr: false })
const CvRingScene = dynamic(() => import("./scenes").then((mod) => mod.CvRingScene), { ssr: false })

const CONTAINER = "mx-auto w-full max-w-7xl px-6 lg:px-8"
const SECTION_Y = "py-20 sm:py-28 lg:py-36"
const H2 = "text-balance text-3xl font-semibold tracking-tight sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]"

type Bezier = [number, number, number, number]
// Strong ease-out for entrances, ease-in-out for elements moving on screen.
const EASE_OUT: Bezier = [0.23, 1, 0.32, 1]
const EASE_IN_OUT: Bezier = [0.77, 0, 0.175, 1]

const PRESSABLE =
  "transition-[transform,background-color,color,box-shadow] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97]"

function useDisplayFont() {
  const { i18n } = useTranslation("home")
  // Arabic glyphs need normal tracking (tight tracking breaks letter joins) and taller line height.
  return i18n.language === "ar" ? "font-arabic !tracking-normal !leading-[1.45]" : "font-poppins"
}

function Reveal({
  children,
  delay = 0,
  className,
  as = "div",
}: {
  children: ReactNode
  delay?: number
  className?: string
  as?: "div" | "li"
}) {
  const reduce = useReducedMotion()
  const Component = as === "li" ? motion.li : motion.div
  return (
    <Component
      className={className}
      initial={{ opacity: 0, transform: reduce ? "translateY(0px)" : "translateY(16px)" }}
      whileInView={{ opacity: 1, transform: "translateY(0px)" }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: reduce ? 0.2 : 0.6, ease: EASE_OUT, delay }}
    >
      {children}
    </Component>
  )
}

function CountUp({
  value,
  start = true,
  duration = 1.2,
  delay = 0,
}: {
  value: number
  start?: boolean
  duration?: number
  delay?: number
}) {
  const reduce = useReducedMotion()
  const motionValue = useMotionValue(0)
  const rounded = useTransform(motionValue, (latest) => Math.round(latest).toString())

  useEffect(() => {
    if (!start) return
    if (reduce) {
      motionValue.set(value)
      return
    }
    const controls = animate(motionValue, value, { duration, delay, ease: EASE_OUT })
    return () => controls.stop()
  }, [start, value, duration, delay, reduce, motionValue])

  return <motion.span className="tabular-nums">{rounded}</motion.span>
}

function CtaLink({
  href,
  variant = "primary",
  tone = "dark",
  children,
}: {
  href: string
  variant?: "primary" | "secondary"
  tone?: "dark" | "light"
  children: ReactNode
}) {
  const styles =
    variant === "primary"
      ? "bg-electric-teal text-[#052620] hover:bg-[#1DD3BA]"
      : tone === "dark"
        ? "bg-white/[0.08] text-white ring-1 ring-inset ring-white/15 hover:bg-white/[0.14]"
        : "bg-[#11163A]/[0.05] text-[#11163A] ring-1 ring-inset ring-[#11163A]/10 hover:bg-[#11163A]/[0.09]"
  const focusOffset = tone === "dark" ? "focus-visible:ring-offset-[#0B1033]" : "focus-visible:ring-offset-white"

  return (
    <Link
      href={href}
      className={`group inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-[15px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-electric-teal focus-visible:ring-offset-2 ${focusOffset} ${styles} ${PRESSABLE}`}
    >
      {children}
      {variant === "primary" && (
        <ArrowRight
          aria-hidden
          className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
        />
      )}
    </Link>
  )
}

function Tilt({ children, className, max = 6 }: { children: ReactNode; className?: string; max?: number }) {
  const reduce = useReducedMotion()
  // Springs carry momentum so the tilt eases out instead of snapping to the pointer.
  const rotateX = useSpring(0, { stiffness: 140, damping: 18 })
  const rotateY = useSpring(0, { stiffness: 140, damping: 18 })

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (reduce || event.pointerType !== "mouse") return
    const rect = event.currentTarget.getBoundingClientRect()
    rotateY.set(((event.clientX - rect.left) / rect.width - 0.5) * max)
    rotateX.set(-((event.clientY - rect.top) / rect.height - 0.5) * max)
  }

  const onPointerLeave = () => {
    rotateX.set(0)
    rotateY.set(0)
  }

  return (
    <div className={`[perspective:1400px] ${className ?? ""}`} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}>
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>{children}</motion.div>
    </div>
  )
}

function DotGrid() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(rgba(255,255,255,0.07)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,#000_40%,transparent_100%)]"
    />
  )
}

function Hero() {
  const { t, i18n } = useTranslation("home")
  const { user, isCompany } = useAuth()
  const reduce = useReducedMotion()
  const display = useDisplayFont()
  const isSeeker = !!user && !isCompany
  const isEmployer = !!user && isCompany

  const enter = (delay: number) => ({
    initial: {
      opacity: 0,
      transform: reduce ? "translateY(0px)" : "translateY(14px)",
      filter: reduce ? "blur(0px)" : "blur(6px)",
    },
    animate: { opacity: 1, transform: "translateY(0px)", filter: "blur(0px)" },
    transition: { duration: 0.7, ease: EASE_OUT, delay },
  })

  const proofPoints = [
    t("features.platformFeatures.coreFeatures"),
    t("features.platformFeatures.aiScoringCriteria"),
    t("features.secureRoleBasedAccess.title"),
  ]

  return (
    <section className="relative isolate overflow-hidden bg-[#0B1033] text-white">
      <DotGrid />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-48 end-[-12%] -z-10 h-[720px] w-[720px] bg-[radial-gradient(closest-side,rgba(0,191,166,0.16),transparent)]"
      />

      <div
        className={`${CONTAINER} grid items-center gap-y-4 pb-10 pt-12 sm:pb-20 sm:pt-20 lg:min-h-[calc(100svh-4rem)] lg:grid-cols-12 lg:gap-x-10 lg:py-16`}
      >
        <div className="relative z-10 lg:col-span-6">
          <motion.h1
            {...enter(0.05)}
            className={`${display} text-balance text-[2.375rem] font-semibold leading-[1.14] tracking-tight sm:text-5xl sm:leading-[1.12] lg:text-[3.25rem] xl:text-[3.5rem]`}
          >
            {t("hero.title")}{" "}
            <span className="relative inline-block whitespace-nowrap pb-2 text-electric-teal">
              {t("hero.titleHighlight")}
              <svg
                aria-hidden
                viewBox="0 0 300 12"
                preserveAspectRatio="none"
                className="absolute -bottom-0.5 start-0 h-3 w-full text-electric-teal/60 rtl:-scale-x-100"
              >
                <motion.path
                  d="M2 9 C 80 3, 160 2, 298 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  initial={{ pathLength: reduce ? 1 : 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.9, ease: EASE_IN_OUT, delay: 0.75 }}
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p {...enter(0.15)} className="mt-6 max-w-xl text-lg leading-relaxed text-[#C3C9EE] rtl:leading-loose">
            {t("hero.description")}
          </motion.p>

          <motion.div {...enter(0.25)} className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {isEmployer && (
              <>
                <CtaLink href="/employer/evaluate-cv">{t("hero.evaluateCV.button")}</CtaLink>
                <CtaLink href="/employer/post-job" variant="secondary">
                  {t("hero.postJob.button")}
                </CtaLink>
              </>
            )}
            {isSeeker && <CtaLink href="/applicant/jobs">{t("hero.browseJobs.button")}</CtaLink>}
            {!user && (
              <>
                <CtaLink href="/employer/post-job">{t("hero.postJob.button")}</CtaLink>
                <CtaLink href="/applicant/jobs" variant="secondary">
                  {t("hero.browseJobs.button")}
                </CtaLink>
              </>
            )}
          </motion.div>

          {isEmployer && (
            <motion.div {...enter(0.3)} className="mt-4">
              <Link
                href="/applicant/jobs"
                className="text-sm font-medium text-[#C3C9EE] underline-offset-4 transition-colors duration-150 hover:text-white hover:underline"
              >
                {t("hero.browseJobs.button")}
              </Link>
            </motion.div>
          )}

          <motion.ul {...enter(0.35)} className="mt-10 flex sm:mt-12 flex-wrap gap-x-6 gap-y-3 text-sm text-[#AEB5E0]">
            {proofPoints.map((point) => (
              <li key={point} className="flex items-center gap-2">
                <CheckCircle2 aria-hidden className="h-4 w-4 text-electric-teal" />
                {point}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="relative lg:col-span-6 lg:h-[min(720px,calc(100svh-8rem))]">
          <div className="relative h-[320px] sm:h-[460px] lg:absolute lg:inset-0 lg:h-auto">
            <HeroScene
              mirrored={i18n.language === "ar"}
              className="absolute -inset-x-6 inset-y-0 lg:-inset-x-16 lg:-inset-y-10 lg:[mask-image:linear-gradient(to_right,transparent,#000_12%)] lg:rtl:[mask-image:linear-gradient(to_left,transparent,#000_12%)]"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

const STEPS = ["post", "score", "shortlist"] as const

function StepItem({
  stepKey,
  index,
  active,
  onActive,
}: {
  stepKey: (typeof STEPS)[number]
  index: number
  active: boolean
  onActive: (index: number) => void
}) {
  const { t } = useTranslation("home")
  const display = useDisplayFont()
  const ref = useRef<HTMLLIElement>(null)
  const inView = useInView(ref, { margin: "-45% 0px -45% 0px" })

  useEffect(() => {
    if (inView) onActive(index)
  }, [inView, index, onActive])

  return (
    <li
      ref={ref}
      className={`relative flex flex-col justify-center border-s border-white/10 py-10 ps-8 transition-opacity duration-500 ease-out lg:min-h-[52vh] lg:py-0 ${active ? "opacity-100" : "opacity-40"}`}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 start-[-1px] w-px origin-top bg-electric-teal transition-transform duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]"
        style={{ transform: active ? "scaleY(1)" : "scaleY(0)" }}
      />
      <span className="text-sm font-medium tabular-nums text-electric-teal">{String(index + 1).padStart(2, "0")}</span>
      <h3 className={`${display} mt-3 text-2xl font-semibold tracking-tight sm:text-3xl`}>
        {t(`howItWorks.steps.${stepKey}.title`)}
      </h3>
      <p className="mt-4 max-w-md text-lg leading-relaxed text-[#C3C9EE]">{t(`howItWorks.steps.${stepKey}.description`)}</p>
    </li>
  )
}

function HowItWorks() {
  const { t } = useTranslation("home")
  const display = useDisplayFont()
  const [step, setStep] = useState(0)

  return (
    <section className={`relative isolate bg-[#0B1033] text-white ${SECTION_Y}`}>
      <DotGrid />
      <div className={CONTAINER}>
        <Reveal className="max-w-2xl">
          <h2 className={`${display} ${H2}`}>{t("howItWorks.title")}</h2>
          <p className="mt-5 text-lg leading-relaxed text-[#C3C9EE]">{t("howItWorks.subtitle")}</p>
        </Reveal>

        <div className="mt-10 grid gap-4 sm:mt-14 lg:mt-20 lg:grid-cols-2 lg:gap-16">
          {/* Sticky on phones too, so the scene follows the step being read below it. */}
          <div className="sticky top-16 z-10 -mx-6 h-[min(300px,40svh)] bg-[#0B1033] sm:h-[400px] lg:top-20 lg:z-auto lg:mx-0 lg:h-[calc(100svh-7rem)] lg:max-h-[760px] lg:bg-transparent">
            <FunnelScene
              step={step}
              className="absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent,#000_12%,#000_92%,transparent)]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-full h-10 bg-gradient-to-b from-[#0B1033] to-transparent lg:hidden"
            />
          </div>
          <ol>
            {STEPS.map((stepKey, index) => (
              <StepItem key={stepKey} stepKey={stepKey} index={index} active={step === index} onActive={setStep} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}

function Stats() {
  const { t } = useTranslation("home")
  const display = useDisplayFont()
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: "-60px" })

  const stats = [
    { value: 85, label: t("stats.short.fasterScreening"), icon: Clock },
    { value: 80, label: t("stats.short.improvedCandidateFit"), icon: Target },
    { value: 100, label: t("stats.short.secureDataHandling"), icon: ShieldCheck },
    { value: 50, label: t("stats.short.hiringCostsSaved"), icon: TrendingUp, prefix: t("stats.short.upTo") },
  ]

  return (
    <section ref={ref} className="bg-[#0B1033] text-white">
      <div className={CONTAINER}>
        <dl className="grid grid-cols-2 border-y border-white/10 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <Reveal
              key={stat.label}
              delay={index * 0.06}
              className={`flex flex-col-reverse justify-end border-white/10 px-4 py-8 sm:px-8 sm:py-10 lg:py-14 ${index % 2 === 1 ? "border-s" : ""} ${index === 2 ? "lg:border-s" : ""} ${index >= 2 ? "border-t lg:border-t-0" : ""}`}
            >
              <dt className="mt-2 flex items-center gap-2 text-sm text-[#AEB5E0]">
                <stat.icon aria-hidden className="h-4 w-4 text-electric-teal" />
                {stat.label}
              </dt>
              <dd className={`${display} text-4xl font-semibold tracking-tight sm:text-5xl`}>
                {stat.prefix && <span className="me-1.5 text-base font-medium text-[#AEB5E0]">{stat.prefix}</span>}
                <CountUp value={stat.value} start={inView} delay={index * 0.06} />
                <span className="text-electric-teal">%</span>
              </dd>
            </Reveal>
          ))}
        </dl>
      </div>
    </section>
  )
}

function WhyChoose() {
  const { t } = useTranslation("home")
  const display = useDisplayFont()

  const items = [
    { icon: Brain, color: "text-midnight-blue", key: "aiMatching" },
    { icon: BarChart3, color: "text-[#00927F]", key: "smartAnalytics" },
    { icon: Users, color: "text-[#E2562C]", key: "streamlinedProcess" },
  ]

  return (
    <section className={`bg-[#F6F7FB] ${SECTION_Y}`}>
      <div className={`${CONTAINER} grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20`}>
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <h2 className={`${display} ${H2} text-[#11163A]`}>
            {t("features.title")}
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-[#4A5078]">{t("features.subtitle")}</p>
        </Reveal>

        <div className="divide-y divide-[#11163A]/10 border-y border-[#11163A]/10">
          {items.map((item, index) => (
            <Reveal key={item.key} delay={index * 0.08} className="grid grid-cols-[auto_1fr] gap-x-5 py-7 sm:py-9 lg:py-10">
              <item.icon aria-hidden className={`mt-1 h-6 w-6 ${item.color}`} />
              <div>
                <h3 className="text-xl font-semibold text-[#11163A]">{t(`features.${item.key}.title`)}</h3>
                <p className="mt-2 max-w-prose leading-relaxed text-[#4A5078]">{t(`features.${item.key}.description`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

const SHOWCASE = [
  {
    key: "aiPoweredCvAnalysis",
    icon: Brain,
    benefits: ["instantScoring", "objectiveDecisions", "batchProcessing", "detailedAnalysis"],
    image:
      "https://images.unsplash.com/photo-1684369175833-4b445ad6bfb5?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  },
  {
    key: "smartComparisonEngine",
    icon: TrendingUp,
    benefits: ["visualComparison", "skillGapAnalysis", "experienceMapping", "teamFitAssessment"],
    image:
      "https://plus.unsplash.com/premium_photo-1701113010522-5d7e58d333ec?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  },
  {
    key: "jobEnhancementAi",
    icon: FileText,
    benefits: ["aiOptimizedDescriptions", "keywordEnhancement", "industryBestPractices", "higherQualityApplicants"],
    image:
      "https://images.unsplash.com/photo-1679639539537-0d2e452890f7?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  },
]

function Showcase() {
  const { t, i18n } = useTranslation("home")
  const display = useDisplayFont()
  const reduce = useReducedMotion()
  const [active, setActive] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Observed outside Tilt: whileInView inside its 3D context never fired on mobile.
  const mediaRef = useRef<HTMLDivElement>(null)
  const mediaInView = useInView(mediaRef, { once: true, margin: "-100px" })
  const feature = SHOWCASE[active]
  const base = `features.interactiveShowcase.${feature.key}`

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const forward = i18n.language === "ar" ? -1 : 1
    let next: number
    if (event.key === "ArrowRight") next = active + forward
    else if (event.key === "ArrowLeft") next = active - forward
    else if (event.key === "ArrowDown") next = active + 1
    else if (event.key === "ArrowUp") next = active - 1
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = SHOWCASE.length - 1
    else return
    event.preventDefault()
    next = (next + SHOWCASE.length) % SHOWCASE.length
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section id="features" className={`bg-white ${SECTION_Y}`}>
      <div className={CONTAINER}>
        <Reveal className="max-w-2xl">
          <h2 className={`${display} ${H2} text-[#11163A]`}>
            {t("features.platformFeatures.title")}
          </h2>
          <p className="mt-4 text-lg text-[#4A5078]">
            {t("features.platformFeatures.coreFeatures")} · {t("features.platformFeatures.aiScoringCriteria")} ·{" "}
            {t("features.platformFeatures.unlimitedPotential")}
          </p>
        </Reveal>

        <Reveal delay={0.08} className="mt-8 sm:mt-10">
          <div
            role="tablist"
            aria-label={t("features.platformFeatures.title")}
            onKeyDown={onKeyDown}
            className="flex flex-col gap-1 rounded-2xl bg-[#F1F2F8] p-1 sm:inline-flex sm:max-w-full sm:flex-row sm:flex-wrap sm:rounded-full"
          >
            {SHOWCASE.map((item, index) => {
              const selected = index === active
              return (
                <button
                  key={item.key}
                  ref={(element) => {
                    tabRefs.current[index] = element
                  }}
                  type="button"
                  role="tab"
                  id={`showcase-tab-${index}`}
                  aria-selected={selected}
                  aria-controls="showcase-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(index)}
                  className={`relative inline-flex h-11 w-full shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-4 text-sm font-medium sm:h-10 sm:w-auto sm:gap-2 sm:rounded-full outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-midnight-blue focus-visible:ring-offset-2 ${selected ? "text-white" : "text-[#4A5078] hover:text-[#11163A]"}`}
                >
                  {selected && (
                    <motion.span
                      layoutId="showcase-pill"
                      className="absolute inset-0 rounded-xl bg-midnight-blue sm:rounded-full"
                      transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                    />
                  )}
                  <item.icon aria-hidden className="relative h-4 w-4" />
                  <span className="relative">{t(`features.interactiveShowcase.${item.key}.title`)}</span>
                </button>
              )
            })}
          </div>
        </Reveal>

        <div
          id="showcase-panel"
          role="tabpanel"
          aria-labelledby={`showcase-tab-${active}`}
          className="mt-10 grid items-center gap-10 sm:mt-14 lg:mt-16 lg:grid-cols-2 lg:gap-20"
        >
          <div className="min-h-[340px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, filter: "blur(4px)", transform: reduce ? "translateY(0px)" : "translateY(8px)" }}
                animate={{
                  opacity: 1,
                  filter: "blur(0px)",
                  transform: "translateY(0px)",
                  transition: { duration: 0.3, ease: EASE_OUT },
                }}
                exit={{ opacity: 0, filter: "blur(4px)", transition: { duration: 0.15, ease: EASE_OUT } }}
              >
                <h3 className={`${display} text-2xl font-semibold tracking-tight text-[#11163A] sm:text-3xl`}>
                  {t(`${base}.title`)}
                </h3>
                <p className="mt-4 max-w-xl text-lg leading-relaxed text-[#4A5078]">{t(`${base}.description`)}</p>
                <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                  {feature.benefits.map((benefit, index) => (
                    <motion.li
                      key={benefit}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25, ease: EASE_OUT, delay: 0.08 + index * 0.04 }}
                      className="flex items-start gap-3 text-[#11163A]"
                    >
                      <CheckCircle2 aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-[#00927F]" />
                      {t(`${base}.benefits.${benefit}`)}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          <div ref={mediaRef}>
          <Tilt max={5}>
          <motion.div
            initial={{ clipPath: reduce ? "inset(0 0 0% 0 round 24px)" : "inset(0 0 100% 0 round 24px)" }}
            animate={mediaInView ? { clipPath: "inset(0 0 0% 0 round 24px)" } : undefined}
            transition={{ duration: 1, ease: EASE_IN_OUT }}
            className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-[#E9EBF5] shadow-[0_40px_80px_-40px_rgba(17,22,58,0.45)]"
          >
            <AnimatePresence initial={false}>
              <motion.img
                key={feature.image}
                src={feature.image}
                alt={t(`${base}.title`)}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0, transform: reduce ? "scale(1)" : "scale(1.04)" }}
                animate={{ opacity: 1, transform: "scale(1)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
              />
            </AnimatePresence>
          </motion.div>
          </Tilt>
          </div>
        </div>
      </div>
    </section>
  )
}

const CRITERIA = [
  { key: "jobFit", score: 95, icon: Target },
  { key: "experience", score: 88, icon: Briefcase },
  { key: "education", score: 92, icon: GraduationCap },
  { key: "skillsMatch", score: 90, icon: Award },
  { key: "technicalProficiency", score: 87, icon: Brain },
  { key: "softSkills", score: 85, icon: Users },
  { key: "careerGrowth", score: 89, icon: TrendingUp },
  { key: "potential", score: 91, icon: Building },
]
const OVERALL_SCORE = 93

function AiCriteria() {
  const { t } = useTranslation("home")
  const display = useDisplayFont()
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: "-120px" })

  return (
    <section className={`bg-[#F6F7FB] ${SECTION_Y}`}>
      <div ref={ref} className={`${CONTAINER} grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20`}>
        <Reveal>
          <h2 className={`${display} ${H2} text-[#11163A]`}>
            {t("features.aiCapabilities.title")}
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-[#4A5078]">{t("features.aiCapabilities.subtitle")}</p>

          <div className="mt-10 flex items-center gap-6">
            <div className="relative h-32 w-32 shrink-0">
              <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#11163A" strokeOpacity="0.08" strokeWidth="10" />
                <motion.circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="#00BFA6"
                  strokeWidth="10"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: inView ? OVERALL_SCORE / 100 : 0 }}
                  transition={{ duration: 1.4, ease: EASE_OUT, delay: 0.2 }}
                />
              </svg>
              <span className={`${display} absolute inset-0 grid place-items-center text-3xl font-semibold text-[#11163A]`}>
                <CountUp value={OVERALL_SCORE} start={inView} duration={1.4} delay={0.2} />
              </span>
            </div>
            <div>
              <p className="font-semibold text-[#11163A]">{t("features.aiCapabilities.criteria.overallScore")}</p>
              <p className="mt-1 text-sm text-[#4A5078]">{t("features.aiCapabilities.sample")}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
        <Tilt max={4}>
        <div className="rounded-3xl bg-white p-6 shadow-[0_1px_2px_rgba(17,22,58,0.06),0_24px_60px_-28px_rgba(17,22,58,0.28)] sm:p-10">
          <p className="text-sm font-medium text-[#6B7199]">{t("features.aiCapabilities.sample")}</p>
          <ul className="mt-6 grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {CRITERIA.map((criterion, index) => (
              <li key={criterion.key}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex min-w-0 items-center gap-2.5 font-medium text-[#11163A]">
                    <criterion.icon aria-hidden className="h-4 w-4 shrink-0 text-[#6B7199]" />
                    <span className="truncate">{t(`features.aiCapabilities.criteria.${criterion.key}`)}</span>
                  </span>
                  <span className="font-semibold text-[#11163A]">
                    <CountUp value={criterion.score} start={inView} delay={0.1 + index * 0.05} />%
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#11163A]/[0.06]">
                  <motion.div
                    className={`h-full origin-left rounded-full rtl:origin-right ${criterion.score >= 90 ? "bg-electric-teal" : "bg-midnight-blue"}`}
                    initial={{ transform: "scaleX(0)" }}
                    animate={{ transform: inView ? `scaleX(${criterion.score / 100})` : "scaleX(0)" }}
                    transition={{ duration: 1, ease: EASE_OUT, delay: 0.1 + index * 0.05 }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
        </Tilt>
        </Reveal>
      </div>
    </section>
  )
}

function Categories() {
  const { t } = useTranslation("home")
  const display = useDisplayFont()
  const prefix = "features.featureCategories"

  const groups = [
    {
      title: t(`${prefix}.forEmployers`),
      icon: Building,
      columns: "sm:grid-cols-2",
      items: [
        { icon: Brain, key: "employerFeatures.aiCvEvaluation" },
        { icon: TrendingUp, key: "employerFeatures.applicantComparison" },
        { icon: FileText, key: "employerFeatures.jobEnhancement" },
        { icon: Clock, key: "employerFeatures.interviewScheduling" },
        { icon: Briefcase, key: "employerFeatures.applicationPipeline" },
        { icon: Users, key: "employerFeatures.talentPoolCrm" },
        { icon: Target, key: "employerFeatures.weightedScoring" },
        { icon: BarChart3, key: "employerFeatures.hiringAnalytics" },
      ],
    },
    {
      title: t(`${prefix}.forJobSeekers`),
      icon: Users,
      columns: "",
      items: [
        { icon: Users, key: "jobSeekerFeatures.profileCreation" },
        { icon: Target, key: "jobSeekerFeatures.oneClickApplications" },
        { icon: Briefcase, key: "jobSeekerFeatures.personalizedRecommendations" },
        { icon: Clock, key: "jobSeekerFeatures.progressTracking" },
      ],
    },
  ]

  return (
    <section className={`bg-white ${SECTION_Y}`}>
      <div className={CONTAINER}>
        <Reveal className="max-w-2xl">
          <h2 className={`${display} ${H2} text-[#11163A]`}>
            {t(`${prefix}.title`)}
          </h2>
          <p className="mt-4 text-lg text-[#4A5078]">{t(`${prefix}.subtitle`)}</p>
        </Reveal>

        <div className="mt-10 grid gap-12 sm:mt-14 sm:gap-16 lg:mt-20 lg:grid-cols-[1.4fr_1fr] lg:gap-20">
          {groups.map((group, groupIndex) => (
            <div key={group.title}>
              <Reveal delay={groupIndex * 0.08} className="flex items-center gap-3 border-b border-[#11163A]/10 pb-4">
                <group.icon aria-hidden className="h-5 w-5 text-midnight-blue" />
                <h3 className="text-xl font-semibold text-[#11163A]">{group.title}</h3>
                <span className="ms-auto text-sm tabular-nums text-[#6B7199]">{group.items.length}</span>
              </Reveal>
              <ul className={`mt-2 grid gap-x-8 ${group.columns}`}>
                {group.items.map((item, index) => (
                  <Reveal as="li" key={item.key} delay={0.05 + index * 0.04} className="flex gap-3 py-3.5 sm:py-4">
                    <item.icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[#00927F]" />
                    <div>
                      <p className="font-medium text-[#11163A]">{t(`${prefix}.${item.key}.title`)}</p>
                      <p className="mt-1 text-sm leading-relaxed text-[#4A5078]">{t(`${prefix}.${item.key}.description`)}</p>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  const { t } = useTranslation("home")
  const display = useDisplayFont()
  const { user, isCompany } = useAuth()

  return (
    <section className="relative isolate overflow-hidden bg-[#0B1033] text-white">
      <DotGrid />
      <div className={`${CONTAINER} pt-20 text-center sm:pt-28 lg:pt-36`}>
        <Reveal className="mx-auto max-w-3xl">
          <h2 className={`${display} text-balance text-3xl font-semibold tracking-tight sm:text-5xl`}>{t("cta.title")}</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-[#C3C9EE]">{t("cta.subtitle")}</p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            {!(user && !isCompany) && <CtaLink href="/employer/post-job">{t("cta.getStarted")}</CtaLink>}
            <CtaLink href="/applicant/jobs" variant={user && !isCompany ? "primary" : "secondary"}>
              {t("hero.browseJobs.button")}
            </CtaLink>
          </div>
        </Reveal>
      </div>
      <div className="relative mx-auto mt-2 h-[250px] max-w-6xl sm:mt-6 sm:h-[380px] lg:h-[460px]">
        <CvRingScene className="absolute inset-0" />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-[#080B26]" />
      </div>
    </section>
  )
}

function SiteFooter() {
  const { t, i18n } = useTranslation("home")
  const { t: legalT } = useTranslation("legal")
  const { user, isCompany, signOut } = useAuth()
  const router = useRouter()
  const isRTL = i18n.language === "ar"

  const handleLogout = async () => {
    try {
      await signOut()
      toast.success("Signed out successfully")
      router.push("/")
    } catch (error) {
      console.error("Logout error:", error)
      toast.error("Failed to sign out")
    }
  }

  const linkClass = "text-sm text-[#AEB5E0] transition-colors duration-150 hover:text-white"
  const headingClass = "text-sm font-semibold text-white"

  return (
    <footer className="bg-[#080B26] text-white">
      <div className={`${CONTAINER} pb-12 pt-16 lg:pt-20`} dir={isRTL ? "rtl" : "ltr"}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="col-span-2 space-y-4 lg:col-span-1">
            <div className="flex items-center gap-2">
              <img src="/visual-identity/logo-icon-night.png" alt="Skill Fits" className="h-8 w-8" />
              <span className="text-xl font-bold">
                <span className="text-white">SKILL</span>
                <span className="text-[#D1D5DB]">FITS</span>
              </span>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#AEB5E0]">{t("footer.companyInfo")}</p>
            <div className="flex gap-4">
              <a href="https://facebook.com/SkillFitsAI" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className={linkClass}>
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" aria-label="X (Twitter)" className={linkClass}>
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://linkedin.com/company/skillfits" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className={linkClass}>
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://instagram.com/skillfits.ai" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={linkClass}>
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className={headingClass}>{t("footer.quickLinks")}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link href="/applicant/jobs" className={linkClass}>
                  {t("footer.browseJobs")}
                </Link>
              </li>
              <li>
                <Link href="/employer/request-free-credit" className={linkClass}>
                  {t("footer.pricing")}
                </Link>
              </li>
              <li>
                <Link href="/contact" className={linkClass}>
                  {t("footer.contactUs")}
                </Link>
              </li>
              {user && isCompany && (
                <>
                  <li>
                    <Link href="/employer/post-job" className={linkClass}>
                      {t("footer.postJob")}
                    </Link>
                  </li>
                  <li>
                    <Link href="/employer/evaluate-cv" className={linkClass}>
                      {t("footer.evaluateCV")}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className={headingClass}>{t("footer.userServices")}</h4>
            <ul className="space-y-2.5">
              {!user && (
                <>
                  <li>
                    <Link href="/auth/applicant/login" className={linkClass}>
                      {t("footer.seekerLogin")}
                    </Link>
                  </li>
                  <li>
                    <Link href="/auth/employer/login" className={linkClass}>
                      {t("footer.companyLogin")}
                    </Link>
                  </li>
                  <li>
                    <Link href="/auth/applicant/signup" className={linkClass}>
                      {t("footer.createAccount")}
                    </Link>
                  </li>
                  <li>
                    <Link href="/auth/employer/signup" className={linkClass}>
                      {t("footer.createCompanyAccount")}
                    </Link>
                  </li>
                </>
              )}
              {user && !isCompany && (
                <li>
                  <Link href="/applicant/dashboard" className={linkClass}>
                    {t("footer.applicantDashboard")}
                  </Link>
                </li>
              )}
              {user && isCompany && (
                <li>
                  <Link href="/employer/dashboard" className={linkClass}>
                    {t("footer.profileManagement")}
                  </Link>
                </li>
              )}
              {user && (
                <li>
                  <button type="button" onClick={handleLogout} className={`${linkClass} text-start`}>
                    {isRTL ? "تسجيل الخروج" : "Sign Out"}
                  </button>
                </li>
              )}
            </ul>
          </div>

          <div className="col-span-2 space-y-4 lg:col-span-1">
            <h4 className={headingClass}>{t("footer.contactUs")}</h4>
            <div className="flex items-center gap-3">
              <Mail aria-hidden className="h-4 w-4 text-electric-teal" />
              <a href="mailto:info@skillfits.com" className={linkClass}>
                info@skillfits.com
              </a>
            </div>
            <div className="flex items-center gap-3">
              <MapPin aria-hidden className="h-4 w-4 text-electric-teal" />
              <span className="text-sm text-[#AEB5E0]">Doha Qatar</span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-sm text-[#AEB5E0]">{t("footer.copyright")}</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link href="/legal/privacy-policy" className={linkClass}>
              {t("footer.privacyPolicy")}
            </Link>
            <Link href="/legal/terms-of-service" className={linkClass}>
              {t("footer.termsOfService")}
            </Link>
            <Link href="/legal/cookie-policy" className={linkClass}>
              {t("footer.cookiePolicy")}
            </Link>
            <Link href="/legal/ai-use-and-automated-processing" className={linkClass}>
              {legalT("navigation.aiNotice")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export function HomeLanding() {
  // overflow-x-clip (not hidden) keeps position: sticky working in How it works.
  return (
    <div className="min-h-screen overflow-x-clip bg-white">
      <Hero />
      <Stats />
      <WhyChoose />
      <HowItWorks />
      <Showcase />
      <AiCriteria />
      <Categories />
      <FinalCta />
      <SiteFooter />
    </div>
  )
}
